/* eslint-disable ember/no-computed-properties-in-native-classes, no-prototype-builtins */
// Extends From
import Service from '@ember/service';
// Ember Imports
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
// Internal Imports
import AttendanceViewMode from '@linc-technologies/linced-common/enums/person-option-attendance-view-mode';
import NotificationPreference from '@linc-technologies/linced-common/enums/person-option-notification-preference';
import SearchMode from '@linc-technologies/linced-common/enums/person-option-search-mode';
import UpdatePersonOptionsProp from '@linc-technologies/linced-common/enums/person-option-update-props';

/**
 * person-options provides a service to update a person's options.
 */
export default class PersonOptionsService extends Service {
  @service
  session;

  @service
  heroApi;

  @service
  toaster;

  // options extracts the current user's personal options.
  @alias('session.person.options')
  options;

  // notificationPreference returns the current person's notification preference.
  @computed('options.notificationPreference', 'session.person')
  get notificationPreference() {
    return this.getNotificationPreference(this.session.person);
  }

  // searchMode returns the current person's default search mode.
  @computed('options.searchMode', 'session.person')
  get searchMode() {
    return this.getSearchMode(this.session.person);
  }

  // attendanceViewMode returns the current person's default search mode.
  @computed('options.attendanceViewMode', 'session.person')
  get attendanceViewMode() {
    return this.getAttendanceViewMode(this.session.person);
  }

  // groupsModes returns the current person's default groups modes.
  @computed(
    'options.{autoGroupsMode,classGroupsMode,myGroupsMode,sharedGroupsMode}',
    'session.person',
  )
  get groupsModes() {
    return {
      my: this.getGroupsMode(this.session.person, 'my'),
      shared: this.getGroupsMode(this.session.person, 'shared'),
      auto: this.getGroupsMode(this.session.person, 'auto'),
      // `class` is a reserved word.
      class: this.getGroupsMode(this.session.person, 'class'),
    };
  }

  // groupMode returns the current person's default group (members) mode.
  @computed('options.groupMode', 'session.person')
  get groupMode() {
    return this.getGroupMode(this.session.person);
  }

  // enrolmentApplicationsCustomFields returns the current person's preferred custom column fields' ids
  // for the enrolment applications screen
  @computed('options.enrolmentApplicationsCustomFields', 'session.person')
  get enrolmentApplicationsCustomFields() {
    return this.getEnrolmentApplicationsFieldIds(this.session.person);
  }

  // dataCardOptions returns the current person's data card options
  @computed('options.dataCards', 'session.person')
  get dataCardOptions() {
    return this.getDataCardOptions(this.session.person);
  }

  // defaultMilestoneSet returns the current person's default milestone set.
  @alias('options.defaultMilestoneSet')
  defaultMilestoneSet;

  // setHideArchivedLineItems sets the line item display mode for the current user.
  setHideArchivedLineItems(mode) {
    return this.setPersonHideArchivedLineItems(this.session.person, mode);
  }

  // setPersonHideArchivedLineItems enables setting the line item display mode
  // for a specific person.
  setPersonHideArchivedLineItems(person, hideArchivedLineItems) {
    return this.updateOptions(person, { hideArchivedLineItems })
      .then(() =>
        this.toaster.success(`Default line item display mode updated.`),
      )
      .catch(() =>
        this.toaster.error(
          'Error saving line item display mode. Please try again.',
        ),
      );
  }

  // hideArchivedLineItems returns the current person's line item display mode
  @alias('options.hideArchivedLineItems')
  hideArchivedLineItems;

  // autosaveNumericGrades returns the current person's autosave preference for
  // numeric grades (in the group grading UI).
  @alias('options.autosaveNumericGrades')
  autosaveNumericGrades;

  // hasAcknowledgedReportDisclaimer returns whether the current person has
  // acknowledged the report writing disclaimer modal.
  @alias('options.hasAcknowledgedReportDisclaimer')
  hasAcknowledgedReportDisclaimer;

  // progressOverview returns the current person's Milestones progress
  // overview settings.
  @computed(
    'options.{progressOverviewMode,progressOverviewYear,progressOverviewControlsMinimised}',
  )
  get progressOverview() {
    return {
      mode: this.options?.progressOverviewMode || 0,
      year: this.options?.progressOverviewYear || '',
      controlsMinimised: !!this.options?.progressOverviewControlsMinimised,
    };
  }

  // setProgressOverview saves any of the current person's progress overview
  // settings ({mode, year, controlsMinimised}). It saves quietly, as the
  // page has already changed; only a failure is reported.
  setProgressOverview(settings) {
    let payload = {};
    if (Object.hasOwn(settings, 'mode')) {
      payload.progressOverviewMode = settings.mode;
    }
    if (Object.hasOwn(settings, 'year')) {
      payload.progressOverviewYear = settings.year;
    }
    if (Object.hasOwn(settings, 'controlsMinimised')) {
      payload.progressOverviewControlsMinimised = settings.controlsMinimised;
    }
    return this.updateOptions(this.session.person, payload).catch(() =>
      this.toaster.error('Error saving your progress overview settings.'),
    );
  }

  // getNotificationPreference returns the provided person's notification
  // preference.
  getNotificationPreference(person) {
    if (!person || !person.options || !person.options.notificationPreference) {
      return NotificationPreference.DEFAULT;
    }

    return person.options.notificationPreference;
  }

  // setNotificationPreference sets the notification preference for the current
  // user.
  setNotificationPreference(preference) {
    return this.setPersonNotificationPreference(
      this.session.person,
      preference,
    );
  }

  // setPersonNotificationPreference enables setting notification preferences
  // for a specific person.
  setPersonNotificationPreference(person, preference) {
    return this.updateOptions(person, { notificationPreference: preference })
      .then(() =>
        this.toaster.success(
          `Notification preference changed to '${NotificationPreference.getLabel(
            preference,
          )}'`,
        ),
      )
      .catch(() =>
        this.toaster.error(
          'Error saving notification preference. Please try again.',
        ),
      );
  }

  // getSearchMode returns the provided person's default search mode.
  getSearchMode(person) {
    if (!person || !person.options || !person.options.searchMode) {
      return SearchMode.GRID;
    }

    return person.options.searchMode;
  }

  // setSearchMode sets the default sort mode for the current user.
  setSearchMode(mode) {
    return this.setPersonSearchMode(this.session.person, mode);
  }

  // setPersonSearchMode enables setting the default search mode
  // for a specific person.
  setPersonSearchMode(person, mode) {
    return this.updateOptions(person, { searchMode: mode })
      .then(() =>
        this.toaster.success(
          `Default search mode changed to '${SearchMode.getLabel(mode)}'`,
        ),
      )
      .catch(() =>
        this.toaster.error(
          'Error saving default search mode. Please try again.',
        ),
      );
  }

  // getAttendanceViewMode returns the provided person's default attendance view mode.
  getAttendanceViewMode(person) {
    if (!person || !person.options || !person.options.attendanceViewMode) {
      return AttendanceViewMode.PERIOD;
    }

    return person.options.attendanceViewMode;
  }

  setAttendanceViewMode(mode) {
    return this.setPersonAttendanceViewMode(this.session.person, mode);
  }

  // setPersonAttendanceViewMode enables setting the default search mode
  // for a specific person.
  setPersonAttendanceViewMode(person, mode) {
    return this.updateOptions(person, { attendanceViewMode: mode })
      .then(() =>
        this.toaster.success(
          `Default view mode changed to '${AttendanceViewMode.getLabel(mode)}'`,
        ),
      )
      .catch(() =>
        this.toaster.error('Error saving default view mode. Please try again.'),
      );
  }

  // getGroupsMode returns the provided person's default mode for the specified
  // type of groups.
  getGroupsMode(person, groupType) {
    let key = `${groupType}GroupsMode`;
    if (!person || !person.options || !person.options[key]) {
      return SearchMode.GRID;
    }

    return person.options[key];
  }

  // setGroupsMode sets the default mode for the specified group type, for the
  // current user.
  setGroupsMode(groupType, mode) {
    const key = `${groupType}GroupsMode`;
    return this.setPersonGroupsMode(this.session.person, key, mode);
  }

  // setPersonGroupsMode enables setting the default mode for the specified
  // group type, for a specific person.
  setPersonGroupsMode(person, key, mode) {
    return this.updateOptions(person, { [key]: mode })
      .then(() =>
        this.toaster.success(
          `Default view changed to '${SearchMode.getLabel(mode)}'`,
        ),
      )
      .catch(() =>
        this.toaster.error('Error saving default view. Please try again.'),
      );
  }

  // getGroupMode returns the provided person's default mode for group members.
  getGroupMode(person) {
    if (!person || !person.options || !person.options.groupMode) {
      return SearchMode.GRID;
    }

    return person.options.groupMode;
  }

  // setGroupMode sets the current user's default mode for group members.
  setGroupMode(mode) {
    return this.setPersonGroupMode(this.session.person, mode);
  }

  // setPersonGroupMode enables setting the default mode for group members,
  // for a specific person.
  setPersonGroupMode(person, mode) {
    return this.updateOptions(person, { groupMode: mode })
      .then(() =>
        this.toaster.success(
          `Default group membership view changed to '${SearchMode.getLabel(
            mode,
          )}'`,
        ),
      )
      .catch(() =>
        this.toaster.error(
          'Error saving default group membership view. Please try again.',
        ),
      );
  }

  // getEnrolmentApplicationsFieldIds returns the provided person's preferred custom column field ids
  // for the enrolment applications screen
  getEnrolmentApplicationsFieldIds(person) {
    if (
      !person ||
      !person.options ||
      !person.options.enrolmentApplicationsCustomFields
    ) {
      return [];
    }

    return person.options.enrolmentApplicationsCustomFields;
  }

  // setEnrolmentApplicationsFields sets the preferred custom columns when
  // viewing the applications to enrol for the current user
  setEnrolmentApplicationsFields(customFields) {
    return this.setPersonEnrolmentApplicationsFields(
      this.session.person,
      customFields,
    );
  }

  // setPersonEnrolmentApplicationsFields sets the preferred custom columns when
  // viewing the applications to enrol for a specific person
  setPersonEnrolmentApplicationsFields(person, customFields) {
    // The order of custom fields must be preserved upon saving
    // We cannot change the order of custom fields in columns,
    // any empty object in the middle of the array is done intentionally,
    // as this is how users interact with the UI
    let fieldIds = customFields.map((fieldObject) => {
      if (Object.keys(fieldObject).length === 0) {
        // Skip an empty field object
        return;
      }
      return fieldObject.field.fieldId;
    });

    // Create the options structure
    const options = { enrolmentApplicationsCustomFields: fieldIds };

    // Create the updateOptionProps array with the appropriate enum value
    const updateOptionProps = [
      UpdatePersonOptionsProp.ENROLMENT_APPLICATIONS_CUSTOM_FIELDS,
    ];

    return this.heroApi.people
      .updatePersonOptions(
        this.session.activeSchool,
        person.id,
        options,
        updateOptionProps,
      )
      .then((result) => {
        this.toaster.success('Custom columns preferences successfully saved.');
        return result;
      })
      .catch((error) => {
        this.toaster.error(
          'Error saving custom columns preferences. Please try again.',
        );
        throw error;
      });
  }

  // getDefaultMilestoneSet returns the provided person's default Milestone Set.
  getDefaultMilestoneSet(person) {
    return person.get('options.defaultMilestoneSet');
  }

  setDefaultMilestoneSet(milestoneSet) {
    return this.setPersonDefaultMilestoneSet(this.session.person, milestoneSet);
  }

  // setDefaultMilestoneSet enables setting the default Milestone Set for a
  // specific person.
  setPersonDefaultMilestoneSet(person, milestoneSet) {
    return this.updateOptions(person, { defaultMilestoneSet: milestoneSet })
      .then(() => this.toaster.success('Default Milestones updated'))
      .catch(() =>
        this.toaster.error(
          'Error saving default Milestones. Please try again.',
        ),
      );
  }

  // getAutosaveNumericGrades returns the provided person's autosave preference
  // for numeric grades in the group grading UI.
  getAutosaveNumericGrades(person) {
    return person.get('options.autosaveNumericGrades');
  }

  setAutosaveNumericGrades(autosaveNumericGrades) {
    return this.setPersonAutosaveNumericGrades(
      this.session.person,
      autosaveNumericGrades,
    );
  }

  // setHasAcknowledgedReportDisclaimer silently marks the current user as
  // having acknowledged the report writing disclaimer.
  setHasAcknowledgedReportDisclaimer() {
    return this.updateOptions(this.session.person, {
      hasAcknowledgedReportDisclaimer: true,
    }).catch(() =>
      this.toaster.error(
        'Error saving acknowledging disclaimer. Please try again.',
      ),
    );
  }

  // setAutosaveNumericGrades enables setting the numeric grade autosave
  // preference for a specific person.
  setPersonAutosaveNumericGrades(person, autosaveNumericGrades) {
    return this.updateOptions(person, { autosaveNumericGrades })
      .then(() => this.toaster.success('Autosave preference updated'))
      .catch(() =>
        this.toaster.error(
          'Error saving autosave preference. Please try again.',
        ),
      );
  }

  /**
   * Data-card options
   *
   **/

  // getDataCardOptions returns the provided person's data card options
  getDataCardOptions(person) {
    // Check if person and options exist
    if (!person || !person.options) {
      return {
        assessmentIds: [],
        assessmentModuleIds: [],
        subjectIds: [],
        progressIds: [],
      };
    }

    const options = person.options;

    // Look for dataCards with capital C (as it appears in the API response)
    if (options.dataCards) {
      return {
        assessmentIds: options.dataCards.assessmentIds || [],
        assessmentModuleIds: options.dataCards.assesmentModuleIds || [],
        subjectIds: options.dataCards.subjectIds || [],
        progressIds: options.dataCards.progressIds || [],
      };
    }

    // Fallback for backward compatibility or lowercase version
    if (options.datacards) {
      return {
        assessmentIds: options.datacards.assessmentIds || [],
        assessmentModuleIds: options.datacards.assesmentModuleIds || [],
        subjectIds: options.datacards.subjectIds || [],
        progressIds: options.datacards.progressIds || [],
      };
    }

    // Last resort: look for values at the root level
    return {
      assessmentIds: options.assessmentIds || [],
      assessmentModuleIds: options.assessmentModuleIds || [],
      subjectIds: options.subjectIds || [],
      progressIds: options.progressIds || [],
    };
  }

  updateAllDataCardOptions(person, optionsMap) {
    // Determine which datacards options are being updated
    const updateOptionProps = [];

    if (optionsMap.assessmentIds) {
      updateOptionProps.push(UpdatePersonOptionsProp.DATACARDS_ASSESSMENT_IDS);
    }

    if (optionsMap.assessmentModuleIds) {
      updateOptionProps.push(
        UpdatePersonOptionsProp.DATACARDS_ASSESSMENT_MODULE_IDS,
      );
    }

    if (optionsMap.subjectIds) {
      updateOptionProps.push(UpdatePersonOptionsProp.DATACARDS_SUBJECT_IDS);
    }

    if (optionsMap.progressIds) {
      updateOptionProps.push(UpdatePersonOptionsProp.DATACARDS_PROGRESS_IDS);
    }

    // Pass options and updateOptionProps directly to heroApi
    // Use dataCards with capital C to match API expectations
    const options = {
      dataCards: { ...optionsMap },
    };

    return this.heroApi.people
      .updatePersonOptions(
        this.session.activeSchool,
        person.id,
        options,
        updateOptionProps,
      )
      .then((result) => {
        this.toaster.success('All preferences successfully saved.');
        return result;
      })
      .catch((error) => {
        this.toaster.error('Error saving preferences. Please try again.');
        throw error;
      });
  }

  getAllDataCardOptions(person, optionKeys) {
    if (!person || !person.options) {
      return {};
    }

    const datacards = person.options.dataCards || {};

    // Map old keys to new keys for backward compatibility
    const keyMappings = {
      assessmentIds: 'assessmentIds',
      assessmentModuleIds: 'assessmentModuleIds',
      subjectIds: 'subjectIds',
      progressIds: 'progressIds',
    };

    return optionKeys.reduce((acc, key) => {
      // Remove 'datacards.' prefix if it exists (for backward compatibility)
      const cleanKey = key.startsWith('dataCards.')
        ? key.replace('dataCards.', '')
        : key;

      // Map old key to new key if applicable
      const newKey = keyMappings[cleanKey] || cleanKey;

      // First try to get from the new datacards object
      if (datacards[newKey]) {
        acc[key] = datacards[newKey];
      }
      // Then fall back to the old format for compatibility during migration
      else if (person.options[cleanKey]) {
        acc[key] = person.options[cleanKey];
      }
      // Default to empty array
      else {
        acc[key] = [];
      }

      return acc;
    }, {});
  }

  /**
   * updateOptions prepares and sends the request to update person options
   * with the new backend format.
   *
   * @param {Object} person - The person whose options are being updated
   * @param {Object} optionsPayload - The options to update
   * @return {Promise} - A promise that resolves when the update is complete
   * @private
   */
  updateOptions(person, optionsPayload) {
    // Map option keys to the appropriate enum values
    const updateOptionProps = [];

    // Check for datacards options
    if (optionsPayload.datacards) {
      const datacards = optionsPayload.datacards;

      if (datacards.assessmentIds) {
        updateOptionProps.push(
          UpdatePersonOptionsProp.DATACARDS_ASSESSMENT_IDS,
        );
      }

      if (datacards.assesmentModuleIds) {
        updateOptionProps.push(
          UpdatePersonOptionsProp.DATACARDS_ASSESSMENT_MODULE_IDS,
        );
      }

      if (datacards.subjectIds) {
        updateOptionProps.push(UpdatePersonOptionsProp.DATACARDS_SUBJECT_IDS);
      }

      if (datacards.progressIds) {
        updateOptionProps.push(UpdatePersonOptionsProp.DATACARDS_PROGRESS_IDS);
      }
    }

    // Check for enrolment applications custom fields
    if (optionsPayload.enrolmentApplicationsCustomFields) {
      updateOptionProps.push(
        UpdatePersonOptionsProp.ENROLMENT_APPLICATIONS_CUSTOM_FIELDS,
      );
    }

    // Check for default Milestone Set.
    if (optionsPayload.defaultMilestoneSet) {
      updateOptionProps.push(UpdatePersonOptionsProp.DEFAULT_MILESTONE_SET);
    }

    // Check for show hideArchivedLineItems (check existence, since it's a bool).
    if (optionsPayload.hasOwnProperty('hideArchivedLineItems')) {
      updateOptionProps.push(UpdatePersonOptionsProp.HIDE_ARCHIVED_LINE_ITEMS);
    }

    // Check for group grading autosave (check existence, since it's a bool).
    if (optionsPayload.hasOwnProperty('autosaveNumericGrades')) {
      updateOptionProps.push(UpdatePersonOptionsProp.AUTOSAVE_NUMERIC_GRADES);
    }

    // Check for report disclaimer acknowledgement (check existence, since it's a bool).
    if (optionsPayload.hasOwnProperty('hasAcknowledgedReportDisclaimer')) {
      updateOptionProps.push(
        UpdatePersonOptionsProp.HAS_ACKNOWLEDGED_REPORT_DISCLAIMER,
      );
    }

    // Check for the progress overview settings (check existence, since the
    // year may be empty and the minimised flag is a bool).
    if (optionsPayload.hasOwnProperty('progressOverviewMode')) {
      updateOptionProps.push(UpdatePersonOptionsProp.PROGRESS_OVERVIEW_MODE);
    }
    if (optionsPayload.hasOwnProperty('progressOverviewYear')) {
      updateOptionProps.push(UpdatePersonOptionsProp.PROGRESS_OVERVIEW_YEAR);
    }
    if (optionsPayload.hasOwnProperty('progressOverviewControlsMinimised')) {
      updateOptionProps.push(
        UpdatePersonOptionsProp.PROGRESS_OVERVIEW_CONTROLS_MINIMISED,
      );
    }

    // Call with separate parameters
    return this.heroApi.people.updatePersonOptions(
      this.session.activeSchool,
      person.id,
      optionsPayload,
      updateOptionProps,
    );
  }
}
